﻿import React, {useCallback, useMemo} from 'react';

/**
 * hook to make interacting with menus less annoying. MUI decided to not use React properly and ignore the ref attribute
 * so you need to create a state variable and create appropriate handlers. this does that for you.
 * See examples here for usage based on MUI's docs: https://mui.com/material-ui/react-menu/
 * Check out the ActionsMenu Stories for examples of using this hook in particular
 */
const useMenu = () => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  // Memoized value for keeping track of whether the menu should be displayed based on the anchor element being set
  const isOpen = useMemo(() => Boolean(anchorEl), [anchorEl]);

  // Callback to be used on the element you want to be the anchor for the menu.
  // it will set the anchor element for you, which will trigger the useMemo in isOpen and update it to true
  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, [setAnchorEl]);

  // Removes anchor element, which triggers useMemo for isOpen to change it to false
  const handleClose = () => {
    setAnchorEl(null);
  };

  return {
    anchorEl,
    isOpen,
    handleClick,
    handleClose
  }
}

export { useMenu };
